const NOTIFICATION_ACTION_PHRASES = [
  'mentioned you in a chat message',
  'mentioned you in a comment',
  'mentioned you in a message',
  'commented on your comment',
  'commented on your message',
  'reacted to your message',
  'reacted to your comment',
  'liked your message',
  'loved your message',
  'haha your message',
  'wow your message',
  'sad your message',
  'angry your message',
  'meme your message',
  'commented on your post',
  'commented on your meme',
  'replied to your comment',
  'replied to your message',
  'mentioned you in',
  'đã thích tin nhắn của bạn',
  'đã react tin nhắn của bạn',
  'đã thích bình luận của bạn',
  'đã react bình luận của bạn',
  'đã bình luận bài viết của bạn',
  'đã trả lời bình luận của bạn',
  'đã trả lời tin nhắn của bạn',
  'đã nhắc tới bạn trong bài viết',
  'đã nhắc đến bạn trong một bình luận',
  'đã mention bạn trong',
] as const

const LEGACY_MESSAGE_REACTION_VERBS: Record<string, string> = {
  liked: '👍',
  loved: '❤️',
  haha: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😡',
}

const CHAT_MESSAGE_PREFIX = /a chat message:\s*/i

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
}

function slugToMentionLabel(slug: string): string {
  const withoutIdSuffix = slug.replace(/\.\d+$/, '')
  const label = withoutIdSuffix.replace(/\./g, ' ').trim()
  return label ? `@${label}` : ''
}

function extractMentionTextFromHtml(value: string): string {
  const completeMention = value.match(/<a[^>]*class="[^"]*mention[^"]*"[^>]*>([\s\S]*?)<\/a>/i)
  if (completeMention?.[1]) {
    return decodeHtmlEntities(completeMention[1].replace(/<[^>]+>/g, ' ')).trim()
  }

  const hrefMatch = value.match(/href="[^"]*\/([^"/]+)"/i)
  if (hrefMatch?.[1]) {
    return slugToMentionLabel(hrefMatch[1])
  }

  const partialAnchorText = value.match(/<a[^>]*>([^<]*)$/i)
  if (partialAnchorText?.[1]?.trim()) {
    return decodeHtmlEntities(partialAnchorText[1]).trim()
  }

  return ''
}

function stripHtmlForNotification(value: string): string {
  const mentionText = extractMentionTextFromHtml(value)

  let text = decodeHtmlEntities(value)
    .replace(/<a[^>]*class="[^"]*mention[^"]*"[^>]*>([\s\S]*?)<\/a>/gi, '$1')
    .replace(/<[^>]+>/g, ' ')
    .replace(/<[^>]*$/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  if (mentionText) {
    if (/a chat message:\s*$/i.test(text)) {
      text = text.replace(/a chat message:\s*$/i, mentionText)
    } else if (!text.includes(mentionText)) {
      text = text.replace(/a chat message:\s*.*$/i, `a chat message: ${mentionText}`)
    }
  }

  text = text.replace(/:\s*a chat message:\s*/i, ': ')
  text = text.replace(CHAT_MESSAGE_PREFIX, '')

  return text.replace(/\s+/g, ' ').trim()
}

function normalizeLegacyReactionSubjects(subject: string): string {
  let normalized = subject

  for (const [verb, emoji] of Object.entries(LEGACY_MESSAGE_REACTION_VERBS)) {
    const legacyPattern = new RegExp(`\\b${verb} your message\\b`, 'gi')
    normalized = normalized.replace(
      legacyPattern,
      `reacted ${emoji} to your message`
    )
  }

  normalized = normalized.replace(
    /\bđã (like|love|haha|wow|sad|angry) bình luận của bạn\b/gi,
    (_match, verb: string) => {
      const emojiMap: Record<string, string> = {
        like: '👍',
        love: '❤️',
        haha: '😂',
        wow: '😮',
        sad: '😢',
        angry: '😡',
      }
      return `đã react ${emojiMap[verb.toLowerCase()] ?? '👍'} bình luận của bạn`
    }
  )

  return normalized
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const REACTION_SUBJECT_PATTERNS = [
  /^(.+?)\s+(reacted(?:\s+.+?)?\s+to your message)(?::\s*(.*))?$/i,
  /^(.+?)\s+(reacted(?:\s+.+?)?\s+to your comment)(?::\s*(.*))?$/i,
  /^(.+?)\s+(đã react(?:\s+.+?)?\s+(?:tin nhắn|bình luận) của bạn)(?::\s*(.*))?$/i,
  /^(.+?)\s+(đã bày tỏ cảm xúc(?:\s+.+?)?\s+với bình luận của bạn)(?::\s*(.*))?$/i,
] as const

function findReactionPhrase(
  text: string
): { actorName: string; actionText: string; remainder: string } | null {
  for (const pattern of REACTION_SUBJECT_PATTERNS) {
    const match = text.match(pattern)
    if (match?.[1] && match[2]) {
      return {
        actorName: match[1].trim(),
        actionText: match[2].trim(),
        remainder: match[3]?.trim() ?? '',
      }
    }
  }

  return null
}

function findActionPhrase(text: string): { actorName: string; actionText: string; endIndex: number } | null {
  const lower = text.toLowerCase()

  for (const phrase of NOTIFICATION_ACTION_PHRASES) {
    const idx = lower.indexOf(phrase.toLowerCase())
    if (idx > 0) {
      return {
        actorName: text.slice(0, idx).trim(),
        actionText: text.slice(idx, idx + phrase.length).trim(),
        endIndex: idx + phrase.length,
      }
    }
  }

  const viActionMatch = text.match(/^(.+?)\s+(đã .+)$/i)
  if (viActionMatch?.[1] && viActionMatch[2]) {
    return {
      actorName: viActionMatch[1].trim(),
      actionText: viActionMatch[2].trim(),
      endIndex: viActionMatch[0].length,
    }
  }

  return null
}

/**
 * Formats notification subject HTML for display.
 * New notifications from fsds-be already include <strong> tags; legacy plain-text
 * subjects are formatted on the client for backwards compatibility.
 */
export function formatNotificationSubjectHtml(subject: string): string {
  if (!subject) return ''

  if (/<strong\b/i.test(subject)) {
    return sanitizeNotificationSubjectHtml(subject)
  }

  return formatLegacyNotificationSubjectHtml(subject)
}

function sanitizeNotificationSubjectHtml(subject: string): string {
  return subject
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<(?!\/?strong\b)[^>]*>/gi, '')
}

function formatLegacyNotificationSubjectHtml(subject: string): string {
  const plain = normalizeLegacyReactionSubjects(stripHtmlForNotification(subject))
  if (!plain) return ''

  const reactionParsed = findReactionPhrase(plain)
  if (reactionParsed) {
    if (reactionParsed.remainder) {
      return `<strong>${escapeHtml(reactionParsed.actorName)}</strong> ${escapeHtml(reactionParsed.actionText)}: <strong>${escapeHtml(reactionParsed.remainder)}</strong>`
    }

    return `<strong>${escapeHtml(reactionParsed.actorName)}</strong> ${escapeHtml(reactionParsed.actionText)}`
  }

  const parsed = findActionPhrase(plain)
  if (!parsed) {
    return escapeHtml(plain)
  }

  let remainder = plain.slice(parsed.endIndex).trim()
  if (remainder.startsWith(':')) {
    remainder = remainder.slice(1).trim()
  }

  if (remainder) {
    return `<strong>${escapeHtml(parsed.actorName)}</strong> ${escapeHtml(parsed.actionText)}: <strong>${escapeHtml(remainder)}</strong>`
  }

  return `<strong>${escapeHtml(parsed.actorName)}</strong> ${escapeHtml(parsed.actionText)}`
}
